import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";

import LandingPage from "./pages/LandingPage";
import LockScreen from "./components/LockScreen";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import KrivaMail from "./pages/KrivaMail";
import MeetingPage from "./pages/MeetingPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import JoinGroupPage from "./pages/JoinGroupPage";
import ServiceManagePage from "./pages/ServiceManagePage";
import AdminServiceApprovalPage from "./pages/AdminServiceApprovalPage";
import CategoryMasterPage from "./pages/CategoryMasterPage";
import SeoCategoryMasterPage from "./pages/SeoCategoryMasterPage";
import FieldMasterPage from "./pages/FieldMasterPage";
import BusinessProfile from "./pages/BusinessProfile";
import PublicProfilePage from "./pages/PublicProfilePage";
import { subscribeToPush } from "./utils/pushSubscribe";
import PushDebug from "./components/PushDebug";
import AdvertisementMaster from "./pages/AdvertisementMaster";
import BlogDetails from "./components/BlogDetails";

import VideoCall from "./components/VideoCall";
import VoiceCall from "./components/VoiceCall";
import GroupVideoCall from "./components/GroupVideoCall";
import GroupCallListener from "./components/call/GroupCallListener";
import GlobalCallListener from "./components/call/GlobalCallListener";
import VoiceCallListener from "./components/call/VoiceCallListener";

import { AUTH_STORAGE_KEY, SERVER_URL, connectSocket } from "./socket";

// ── Theme & background restore ────────────────────────────────────────────────
const savedTheme = localStorage.getItem("app-theme");
const savedBg    = localStorage.getItem("app-bg-image");

if (savedTheme) document.documentElement.setAttribute("data-theme", savedTheme);

if (savedBg) {
  document.body.style.backgroundImage = `
    linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)),
    url(${savedBg})
  `;
  document.body.style.backgroundSize       = "cover";
  document.body.style.backgroundRepeat     = "no-repeat";
  document.body.style.backgroundPosition   = "center center";
  document.body.classList.add("has-custom-bg");
  document.body.style.minHeight            = "100vh";
  document.body.style.backgroundAttachment = "fixed";
}


function App() {
  const [auth,         setAuth]         = useState(null);
  const [authLoading,  setAuthLoading]  = useState(true);
  const [videoCall,    setVideoCall]    = useState(null);
  const [voiceCall,    setVoiceCall]    = useState(null);
  const [callKey,      setCallKey]      = useState(0);
  const [videoCallKey, setVideoCallKey] = useState(0);
  const [showForceLogout,   setShowForceLogout]   = useState(false);
  const [forceLogoutMsg,    setForceLogoutMsg]     = useState("");
  const [showSessionReq,    setShowSessionReq]     = useState(false);  // Device 1 popup
  const [sessionReqEmail,   setSessionReqEmail]    = useState("");

  // ── Lock state ────────────────────────────────────────────────────────────
  // Lock screen only shows when user explicitly locks, not on page refresh
  const isAppLockEnabled = localStorage.getItem("app-lock-enabled") === "true";
  const [isLocked, setIsLocked] = useState(false);

  // On mount or when app lock enabled changes, set unlocked by default
  useEffect(() => {
    if (isAppLockEnabled) {
      setIsLocked(false);
      sessionStorage.setItem("app-lock-unlocked", "1");
    } else {
      setIsLocked(false);
    }
  }, [isAppLockEnabled]);

  const lock = () => {
    setIsLocked(true);
    sessionStorage.removeItem("app-lock-unlocked");
  };
  const unlock = () => {
    setIsLocked(false);
    sessionStorage.setItem("app-lock-unlocked", "1");
  };

  const currentUser = auth?.user?.email || "";

  // ── Load auth from localStorage ───────────────────────────────────────────
  useEffect(() => {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      try { setAuth(JSON.parse(stored)); } catch {}
    }
    setAuthLoading(false);
  }, []);

  // Clear session lock flag on logout
  useEffect(() => {
    if (!auth?.token) {
      sessionStorage.removeItem("app-lock-unlocked");
    }
  }, [auth]);

  // ── DB lock sync — runs once after auth is confirmed ─────────────────────
  useEffect(() => {
    if (!auth?.token) return;

    const syncLockFromDB = async () => {
      try {
        const res = await fetch(`${SERVER_URL}/api/settings/app-lock`, {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        if (!res.ok) return;
        const data = await res.json();

        if (data.enabled) {
          localStorage.setItem("app-lock-enabled", "true");
          localStorage.setItem("app-lock-type",    data.type    || "pin");
          localStorage.setItem("app-lock-pin",     data.pin     || "");
          localStorage.setItem("app-lock-pattern", data.pattern || "");
        } else {
          localStorage.removeItem("app-lock-enabled");
          localStorage.removeItem("app-lock-pin");
          localStorage.removeItem("app-lock-pattern");
          localStorage.removeItem("app-lock-type");
          setIsLocked(false);
        }
      } catch {
        // Network issue — keep localStorage state as fallback
      }
    };

    syncLockFromDB();
  }, [auth]);

  // ── Ctrl+L shortcut ───────────────────────────────────────────────────────
  useEffect(() => {
    const handleKey = (e) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key.toLowerCase() === "l" &&
        localStorage.getItem("app-lock-enabled") === "true"
      ) {
        e.preventDefault();
        lock();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  useEffect(() => {
  // Wait a bit for SW to be ready before subscribing
  const timer = setTimeout(() => {
    subscribeToPush();
  }, 3000);

  return () => clearTimeout(timer);
}, []); // run once on mount

// ── Handle notification reply action ──────────────────────────────────────
useEffect(() => {
  if (!('serviceWorker' in navigator)) return;

  const handleServiceWorkerMessage = (event) => {
    const { type, action, replyText, notificationData } = event.data || {};
    
    if (type === 'NOTIFICATION_REPLY' && action === 'reply') {
      // Store sender info for Krivachat to use
      const senderEmail = notificationData?.senderEmail;
      const senderName = notificationData?.senderName;
      
      if (senderEmail) {
        sessionStorage.setItem('notification_reply_sender', JSON.stringify({
          email: senderEmail,
          name: senderName,
          replyText: replyText || '',
          timestamp: Date.now()
        }));
      }
      
      // Navigate to chat page so Krivachat mounts and handles stored reply
      try {
        if (window.location.pathname !== '/kmail/krivachat') {
          window.location.href = '/kmail/krivachat';
        }
      } catch (e) {
        console.warn('Failed to navigate to chat page:', e);
      }

      // Dispatch custom event so pages/components can handle the reply
      window.dispatchEvent(new CustomEvent('notification-reply-action', {
        detail: { 
          notificationData,
          replyText: replyText || '',
          senderEmail
        }
      }));
    }
  };

  navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
  
  return () => {
    navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
  };
}, []);

  // ── Expose lock() globally ────────────────────────────────────────────────
  useEffect(() => {
    window.__lockApp = lock;
    return () => { delete window.__lockApp; };
  }, []);

  // ── Helper: read duration from window.__callStartTime ────────────────────
  // window.__callStartTime is set in Krivachat.jsx when a call button is clicked.
  // App.jsx reads it here on call end and resets it.
  const getCallDuration = () => {
    if (!window.__callStartTime) return 0;
    const secs = Math.round((Date.now() - window.__callStartTime) / 1000);
    window.__callStartTime = null; // reset after reading
    return secs;
  };

  // ── Private route ─────────────────────────────────────────────────────────
  const PrivateRoute = ({ children }) => {
    if (authLoading) return null;
    return auth?.token ? children : <Navigate to="/login" />;
  };

useEffect(() => {
  const socket = connectSocket();

  const handleForceLogout = ({ reason }) => {
    console.warn("[SESSION] Force logout:", reason);

    // Show notification before logging out
    setForceLogoutMsg(reason || "You were logged in from another device.");
    setShowForceLogout(true);

    // Auto logout after 4 seconds
    setTimeout(() => {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      socket.disconnect();
      window.location.href = "/login";
    }, 4000);
  };

  socket.on("force_logout", handleForceLogout);

  return () => {
    socket.off("force_logout", handleForceLogout);
  };
}, []);



  // Floating lock button (bottom right)
  // const floatingLockBtn = !isLocked && isAppLockEnabled ? (
  //   <button
  //     onClick={lock}
  //     style={{
  //       position: "fixed",
  //       right: 18,
  //       bottom: 18,
  //       zIndex: 9999,
  //       background: "#1e40af",
  //       color: "#fff",
  //       border: "none",
  //       borderRadius: "50%",
  //       width: 54,
  //       height: 54,
  //       boxShadow: "0 4px 16px rgba(30,64,175,0.18)",
  //       display: "flex",
  //       alignItems: "center",
  //       justifyContent: "center",
  //       fontSize: 26,
  //       cursor: "pointer",
  //     }}
  //     title="Lock App"
  //     aria-label="Lock App"
  //   >
  //     <i className="fa-solid fa-lock" />
  //   </button>
  // ) : null;

  if (authLoading) return <div>Loading...</div>;


  return (
    <>
      {/* Lock screen sits above everything — no z-index fight needed */}
      {isLocked && (
        <LockScreen
          onUnlock={unlock}
          appName="KrivaOne"
        />
      )}
      {/* {floatingLockBtn} */}

      <BrowserRouter>

        {/* ── Call Listeners — only when authenticated ── */}
        {auth?.token && (
          <>
            <GroupCallListener
              setVideoCall={setVideoCall}
              setVoiceCall={setVoiceCall}
            />
            <GlobalCallListener
              key={videoCallKey}
              setVideoCall={(call) => {
                setVideoCall(call);
                if (call === null) setVideoCallKey(k => k + 1);
              }}
            />
            <VoiceCallListener
              key={callKey}
              setVoiceCall={(call) => {
                setVoiceCall(call);
                if (call === null) setCallKey(k => k + 1);
              }}
            />
          </>
        )}

        {/* ── Video Call ── */}
        {videoCall && (
          videoCall.isGroup ? (
            <GroupVideoCall
              group={videoCall.group}
              type={videoCall.type || "video"}
              onEnd={() => setVideoCall(null)}
            />
          ) : (
            <VideoCall
              localUser={currentUser}
              remoteUser={videoCall.remoteUser}
              isCaller={videoCall.isCaller}
              incomingOffer={videoCall.incomingOffer}
              onEnd={() => {
                const duration = getCallDuration();
                window.__saveCall?.({
                  caller:    currentUser,
                  receiver:  videoCall.remoteUser || "",
                  type:      "video",
                  status:    duration > 3 ? "answered" : "missed",
                  duration,
                  groupId:   null,
                  groupName: null,
                });
                setVideoCall(null);
                setVideoCallKey(k => k + 1);
              }}
            />
          )
        )}

        {/* ── Voice Call ── */}
        {voiceCall && (
          voiceCall.isGroup ? (
            <GroupVideoCall
              group={voiceCall.group}
              type="voice"
              onEnd={() => setVoiceCall(null)}
            />
          ) : (
            <VoiceCall
              localUser={currentUser}
              remoteUser={voiceCall.remoteUser}
              isCaller={voiceCall.isCaller}
              incomingOffer={voiceCall.incomingOffer}
              onEnd={() => {
                const duration = getCallDuration();
                window.__saveCall?.({
                  caller:    currentUser,
                  receiver:  voiceCall.remoteUser || "",
                  type:      "voice",
                  status:    duration > 3 ? "answered" : "missed",
                  duration,
                  groupId:   null,
                  groupName: null,
                });
                setVoiceCall(null);
                setCallKey(k => k + 1);
              }}
            />
          )
        )}

        <Routes>
          <Route path="/"               element={<LandingPage  setVoiceCall={setVoiceCall} />} />
          <Route path="/login"          element={<LoginPage />} />
          <Route path="/register"       element={<RegisterPage />} />
          <Route path="/kmail/*"
            element={
              <PrivateRoute>
                <KrivaMail
                  auth={auth}
                  setVideoCall={setVideoCall}
                  setVoiceCall={setVoiceCall}
                />
              </PrivateRoute>
            }
          />
           <Route
    path="/blog/:slug"
    element={<BlogDetails />}
  />
          <Route path="/join-group/:groupId" element={<JoinGroupPage />} />

<Route
  path="/admin/advertisements"
  element={<AdvertisementMaster />}
/>
          <Route path="/add-service"    element={<ServiceManagePage />} />
          <Route path="/admin/service"    element={<AdminServiceApprovalPage />} />
          <Route path="/profile" element={<PublicProfilePage />} />
          <Route path="/:slugOrId" element={<BusinessProfile />} />
          <Route path="/admin/service-categories" element={<CategoryMasterPage />} />
          <Route path="/admin/seo-categories" element={<SeoCategoryMasterPage />} />
          <Route path="/admin/fields" element={<FieldMasterPage />} />
          <Route path="/meeting"        element={<MeetingPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        </Routes>

      </BrowserRouter>

      {showForceLogout && (
  <div style={{
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.85)",
    backdropFilter: "blur(6px)",
    zIndex: 99999,
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 20,
  }}>
    <div style={{
      background: "#0f172a",
      border: "1px solid #ef4444",
      borderRadius: 16,
      padding: "36px 32px",
      maxWidth: 380, width: "100%",
      textAlign: "center",
      boxShadow: "0 24px 64px rgba(239,68,68,0.25)",
    }}>
      {/* Icon */}
      <div style={{
        width: 64, height: 64, borderRadius: "50%",
        background: "rgba(239,68,68,0.15)",
        border: "2px solid rgba(239,68,68,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center",
        margin: "0 auto 20px",
        fontSize: 28,
      }}>
        🔒
      </div>

      <h2 style={{ color: "#f1f5f9", fontSize: 20, fontWeight: 700, margin: "0 0 10px" }}>
        Session Ended
      </h2>

      <p style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.6, margin: "0 0 20px" }}>
        {forceLogoutMsg}
      </p>

      {/* Auto countdown bar */}
      <div style={{
        height: 4, background: "#1e293b",
        borderRadius: 999, overflow: "hidden", marginBottom: 20,
      }}>
        <div style={{
          height: "100%",
          background: "linear-gradient(90deg,#ef4444,#f97316)",
          borderRadius: 999,
          animation: "shrink 4s linear forwards",
        }} />
      </div>

      <p style={{ color: "#64748b", fontSize: 12, margin: "0 0 20px" }}>
        Redirecting to login in 4 seconds...
      </p>

      {/* Manual logout button */}
      <button
        onClick={() => {
          localStorage.removeItem(AUTH_STORAGE_KEY);
          window.location.href = "/login";
        }}
        style={{
          width: "100%", padding: "11px 0",
          background: "linear-gradient(135deg,#ef4444,#dc2626)",
          color: "#fff", border: "none", borderRadius: 10,
          fontWeight: 700, fontSize: 14, cursor: "pointer",
          boxShadow: "0 4px 16px rgba(239,68,68,0.3)",
        }}
      >
        Logout Now
      </button>
    </div>

    {/* CSS for the shrink animation */}
    <style>{`
      @keyframes shrink {
        from { width: 100%; }
        to   { width: 0%; }
      }
    `}</style>
  </div>
)}

    </>
    
  );
}

export default App;
